"use client";

type DesktopInvoke = <T = unknown>(
  command: string,
  args?: Record<string, unknown>,
) => Promise<T>;

type DesktopWindow = Window & {
  __TAURI_INTERNALS__?: {
    invoke?: DesktopInvoke;
  };
  __FINDIN_DESKTOP_CAPABILITIES__?: {
    nativeFileDialogs?: boolean;
    nativeFileDialogsVersion?: number;
  };
};

type SelectedLocalFile = {
  token: string;
  name: string;
  size: number;
  lastModified: number;
};

export type NativeSelectionIssueCode = "FILE_SIZE_EXCEEDED" | "BATCH_SIZE_EXCEEDED";

type NativeSelectionIssue = {
  code: NativeSelectionIssueCode;
  fileName?: string | null;
  actualBytes: number;
  limitBytes: number;
};

type NativePdfSelectionResult = {
  files: SelectedLocalFile[];
  issue?: NativeSelectionIssue | null;
};

export type NativeSelectionKind = "files" | "folder";
export type NativeSaveResult = "saved" | "cancelled" | "unavailable";

const NATIVE_READ_TIMEOUT_MS = 60_000;
const SAVE_CHUNK_BYTES = 512 * 1024;

export class NativeSaveError extends Error {
  readonly destinationAccepted: boolean;

  constructor(message: string, destinationAccepted: boolean, options?: ErrorOptions) {
    super(message, options);
    this.name = "NativeSaveError";
    this.destinationAccepted = destinationAccepted;
  }
}

export class NativeSelectionError extends Error {
  readonly code: NativeSelectionIssueCode;
  readonly fileName?: string;
  readonly actualBytes: number;
  readonly limitBytes: number;

  constructor(issue: NativeSelectionIssue) {
    const message = issue.code === "FILE_SIZE_EXCEEDED"
      ? `本地文件 ${issue.fileName || "PDF"} 超出桌面端读取限制。`
      : "本次选择文件总大小超过桌面端读取限制，请拆分批次后重试。";
    super(message);
    this.name = "NativeSelectionError";
    this.code = issue.code;
    this.fileName = issue.fileName || undefined;
    this.actualBytes = issue.actualBytes;
    this.limitBytes = issue.limitBytes;
  }
}

function getDesktopInvoke(): DesktopInvoke | null {
  if (typeof window === "undefined") {
    return null;
  }
  const invoke = (window as DesktopWindow).__TAURI_INTERNALS__?.invoke;
  return typeof invoke === "function" ? invoke : null;
}

export function isNativeFileDialogRuntime() {
  if (typeof window === "undefined") {
    return false;
  }
  const desktopWindow = window as DesktopWindow;
  return getDesktopInvoke() !== null
    && desktopWindow.__FINDIN_DESKTOP_CAPABILITIES__?.nativeFileDialogs === true;
}

export function isNativeUploadDialogRuntime() {
  if (!isNativeFileDialogRuntime()) {
    return false;
  }
  const desktopWindow = window as DesktopWindow;
  return (desktopWindow.__FINDIN_DESKTOP_CAPABILITIES__?.nativeFileDialogsVersion ?? 0) >= 2;
}

async function invokeWithTimeout<T>(
  invoke: DesktopInvoke,
  command: string,
  args: Record<string, unknown> | undefined,
  timeoutMs: number,
) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      invoke<T>(command, args),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error("读取本地文件超时，请重新选择后重试。"));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

function toUint8Array(value: ArrayBuffer | Uint8Array | number[]) {
  if (value instanceof Uint8Array) {
    return value;
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  if (Array.isArray(value)) {
    return new Uint8Array(value);
  }
  throw new Error("无法解析本地文件数据。");
}

export async function pickNativePdfFiles(options: {
  selectionKind: NativeSelectionKind;
  maxFiles: number;
  maxFileSizeMb: number;
}): Promise<File[] | null> {
  if (!isNativeUploadDialogRuntime()) {
    return null;
  }
  const invoke = getDesktopInvoke();
  if (!invoke) {
    return null;
  }

  const maxFileSizeMb = Number.isFinite(options.maxFileSizeMb) && options.maxFileSizeMb > 0
    ? options.maxFileSizeMb
    : 1;
  const maxFileBytes = Math.max(
    1,
    Math.min(Number.MAX_SAFE_INTEGER, Math.floor(maxFileSizeMb * 1024 * 1024)),
  );
  const selection = await invoke<NativePdfSelectionResult>("select_local_pdfs_v2", {
    selectionKind: options.selectionKind,
    maxFiles: Math.max(1, Math.floor(options.maxFiles)),
    maxFileBytes,
  });
  if (selection.issue) {
    throw new NativeSelectionError(selection.issue);
  }
  const selected = selection.files;
  const tokens = selected.map((item) => item.token);
  const files: File[] = [];
  try {
    for (const item of selected) {
      const value = await invokeWithTimeout<ArrayBuffer | Uint8Array | number[]>(
        invoke,
        "read_selected_local_file",
        { token: item.token },
        NATIVE_READ_TIMEOUT_MS,
      );
      const bytes = toUint8Array(value);
      if (bytes.byteLength !== item.size) {
        throw new Error(`本地文件 ${item.name} 在读取过程中发生变化，请重新选择。`);
      }
      const buffer = new ArrayBuffer(bytes.byteLength);
      new Uint8Array(buffer).set(bytes);
      files.push(new File([buffer], item.name, {
        type: "application/pdf",
        lastModified: item.lastModified || Date.now(),
      }));
    }
    return files;
  } finally {
    if (tokens.length) {
      try {
        await invoke("release_selected_local_files", { tokens });
      } catch (error) {
        console.warn("[Findin Desktop] failed to release native file authorizations", error);
      }
    }
  }
}

function bytesToBase64(bytes: Uint8Array) {
  let binary = "";
  const stride = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += stride) {
    const slice = bytes.subarray(offset, Math.min(bytes.length, offset + stride));
    binary += String.fromCharCode.apply(null, Array.from(slice));
  }
  return globalThis.btoa(binary);
}

async function cancelSaveQuietly(invoke: DesktopInvoke, token: string) {
  try {
    await invoke("cancel_native_save", { token });
  } catch (error) {
    console.warn("[Findin Desktop] failed to clean native save session", error);
  }
}

export async function saveBlobWithNativeDialog(
  blob: Blob,
  suggestedName: string,
): Promise<NativeSaveResult> {
  if (!isNativeFileDialogRuntime()) {
    return "unavailable";
  }
  const invoke = getDesktopInvoke();
  if (!invoke) {
    return "unavailable";
  }

  let token: string | null;
  try {
    token = await invoke<string | null>("begin_native_save", { suggestedName });
  } catch (error) {
    throw new NativeSaveError("无法打开系统保存对话框。", false, { cause: error });
  }
  if (!token) {
    return "cancelled";
  }

  try {
    for (let offset = 0; offset < blob.size; offset += SAVE_CHUNK_BYTES) {
      const chunk = new Uint8Array(
        await blob.slice(offset, Math.min(blob.size, offset + SAVE_CHUNK_BYTES)).arrayBuffer(),
      );
      await invoke("append_native_save_chunk", {
        token,
        chunkBase64: bytesToBase64(chunk),
      });
    }
    await invoke("complete_native_save", { token });
    return "saved";
  } catch (error) {
    await cancelSaveQuietly(invoke, token);
    throw new NativeSaveError("文件保存失败，请重新选择位置后重试。", true, { cause: error });
  }
}
